import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PRODUCT_FORMATS } from '@commerce/shared';
import { api, ApiClientError } from '../../lib/api.js';
import type { Concept, ResearchItem } from '../../lib/types.js';

export function ConceptCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '',
    consumerNeed: '',
    targetAudience: '',
    differentiation: '',
    productFormats: [] as string[],
    researchItemIds: [] as string[],
  });

  const reviewed = useQuery({
    queryKey: ['research', { status: 'reviewed' }],
    queryFn: () =>
      api.get<ResearchItem[]>('/research?status=reviewed&pageSize=100') as Promise<{
        data: ResearchItem[];
      }>,
  });

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Concept>('/concepts', body),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['concepts'] });
      navigate(`/concepts/${res.data.id}`);
    },
    onError: (err) =>
      setError(err instanceof ApiClientError ? err.message : 'Failed to create concept'),
  });

  function toggleResearch(id: string) {
    setForm((f) => ({
      ...f,
      researchItemIds: f.researchItemIds.includes(id)
        ? f.researchItemIds.filter((x) => x !== id)
        : [...f.researchItemIds, id],
    }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate(form);
  }

  return (
    <section>
      <h1>New concept</h1>
      <form className="form" onSubmit={onSubmit}>
        <label htmlFor="title">Title</label>
        <input
          id="title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />

        <label htmlFor="consumerNeed">Consumer need</label>
        <textarea
          id="consumerNeed"
          rows={2}
          value={form.consumerNeed}
          onChange={(e) => setForm({ ...form, consumerNeed: e.target.value })}
          required
        />

        <label htmlFor="targetAudience">Target audience</label>
        <input
          id="targetAudience"
          value={form.targetAudience}
          onChange={(e) => setForm({ ...form, targetAudience: e.target.value })}
          required
        />

        <label htmlFor="differentiation">Differentiation (original, no third-party IP)</label>
        <textarea
          id="differentiation"
          rows={2}
          value={form.differentiation}
          onChange={(e) => setForm({ ...form, differentiation: e.target.value })}
          required
        />

        <label htmlFor="productFormats">Product formats</label>
        <select
          id="productFormats"
          multiple
          value={form.productFormats}
          onChange={(e) =>
            setForm({
              ...form,
              productFormats: Array.from(e.target.selectedOptions, (o) => o.value),
            })
          }
        >
          {PRODUCT_FORMATS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>

        <fieldset>
          <legend>Source research (reviewed)</legend>
          {reviewed.data?.data.map((r) => (
            <label key={r.id} className="check-inline">
              <input
                type="checkbox"
                checked={form.researchItemIds.includes(r.id)}
                onChange={() => toggleResearch(r.id)}
              />
              {r.title}
            </label>
          ))}
        </fieldset>

        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Create draft'}
        </button>
      </form>
    </section>
  );
}
